/**
 * Снимок для гаданий по фото.
 *
 * Ужимаем на устройстве, а не на сервере: снимок с телефона — это 3–8 МБ,
 * а модели со зрением от большей стороны 1280 px пользы уже нет. Разница
 * не в трафике даже, а в том, что мобильная сеть в Баку такой файл может
 * и не дотащить, — а человек в это время смотрит на «отправляю».
 */

/** Большая сторона снимка после сжатия. */
const MAX_SIDE = 1280

/** Качество JPEG. 0.82 — граница, за которой узор гущи начинает мылиться. */
const QUALITY = 0.82

/**
 * Читает выбранный файл и отдаёт data-ссылку на ужатый JPEG.
 *
 * Всегда пересобираем в JPEG, даже если исходник уже JPEG: сервер принимает
 * только его, а телефоны отдают и HEIC, и PNG.
 */
export async function preparePhoto(file: File): Promise<string> {
  const bitmap = await loadBitmap(file)
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height))
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas недоступен')
  ctx.drawImage(bitmap, 0, 0, width, height)
  if ('close' in bitmap) bitmap.close()

  return canvas.toDataURL('image/jpeg', QUALITY)
}

/**
 * Картинка из файла. `createImageBitmap` быстрее и не держит DOM-узел, но в
 * старых Safari его нет — там разбираем через <img> и object URL.
 */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file)
    } catch {
      // HEIC Safari так не открывает — падаем на путь через <img>.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('снимок не читается'))
      img.src = url
    })
  } finally {
    // Отзываем сразу: к моменту resolve картинка уже декодирована.
    URL.revokeObjectURL(url)
  }
}
